import { type JSX, Show } from 'solid-js';
import { t, tPlural } from '../i18n';
import { humanTokens } from '../lib/contextWindow';
import type { ProviderModelOption } from '../lib/keys';
import type { ModelTagsController } from '../lib/modelTags';
import { Field } from './Field';
import { providerLabel } from './KeyCard';
import { ModelTierList } from './ModelTierList';
import { Select } from './Select';
import { Button, buttonClass } from './ui/Button';

// Phase 2 of <AddKeyModal/> (arc 101 slice 02): pick the key's own model from
// what the provider ANSWERED, then optionally tag models to lane tiers.
// Presenter; `createKeyCapture` owns the state and the single POST.
//
// The model select keeps `id="add-key-model"` and the `account_keys.model`
// label the free-text input had: it is the same field on the wire
// (`byok_keys.model`), only its vocabulary changed from "whatever you type" to
// "what this credential can address".
//
// It opens on its PLACEHOLDER and is never defaulted to the first option — the
// `<FleetRolloutModal/>` `channel` rule. A pre-picked model is a model the
// reader did not choose, and this field is what every lane runs on.
//
// Back is offered because a typo'd key or endpoint is the ordinary way to reach
// an empty or wrong list, and re-opening the dialog would drop the secret.
//
// Each option carries the window the PROVIDER reported, and only that: a model
// the provider said nothing about reads as its bare id. This field decides what
// every lane runs on, so "200K" and "we don't know" must not look the same.

export type AddKeyModelsStepProps = {
  /** Raw provider enum value — brand-cased for the count line. */
  readonly provider: string;
  readonly models: readonly ProviderModelOption[];
  readonly model: string;
  readonly onModel: (value: string) => void;
  readonly tags: ModelTagsController;
  readonly busy: boolean;
  /** The capture mutation's own failure, owned by the route container. */
  readonly error: string | null;
  readonly onBack: () => void;
};

/** `claude-sonnet-5 — 200K context`, or the bare id when none was reported. */
function optionLabel(option: ProviderModelOption): string {
  return option.context_length === undefined
    ? option.id
    : t('account_keys.model_option_context', {
        model: option.id,
        size: humanTokens(option.context_length),
      });
}

export function AddKeyModelsStep(props: AddKeyModelsStepProps): JSX.Element {
  return (
    <>
      <div class="field-group">
        <p class="field__desc">
          {tPlural('account_keys.models_found', props.models.length, {
            count: props.models.length,
            provider: providerLabel(props.provider),
          })}
        </p>
        <Field
          label={t('account_keys.model')}
          id="add-key-model"
          desc={t('account_keys.model_desc')}
        >
          {(a11y) => (
            <Select
              a11y={a11y}
              label={t('account_keys.model')}
              block
              value={props.model}
              options={[
                { value: '', label: t('account_keys.model_select_placeholder') },
                ...props.models.map((option) => ({ value: option.id, label: optionLabel(option) })),
              ]}
              onChange={props.onModel}
            />
          )}
        </Field>
        <Field
          as="fieldset"
          label={t('account_keys.tiers_title')}
          id="add-key-tiers"
          desc={t('account_keys.tiers_desc')}
        >
          {() => <ModelTierList models={props.models} tags={props.tags} />}
        </Field>
      </div>
      <Show when={props.error}>
        <p class="field__error" role="alert">
          {props.error}
        </p>
      </Show>
      <div class="dialog__footer">
        <Button variant="outline" onClick={props.onBack}>
          {t('account_keys.back')}
        </Button>
        <button type="submit" class={buttonClass()} disabled={props.busy || props.model === ''}>
          {props.busy ? t('account_keys.adding') : t('account_keys.add_submit')}
        </button>
      </div>
    </>
  );
}
